<template>
  <div class="container">
    <!-- 卡片视图区域 -->
    <el-card class="box-card" :body-style="{ padding: '0' }">
      <!-- 表格头部操作栏 -->
      <div style="display: flex; flex-wrap: wrap; align-items: center; gap: 16px; padding: 0; width: 100%;">
        <!-- 日期选择器 -->
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          @change="handleDateChange"
          value-format="yyyy-MM-dd HH:mm:ss"
          style="width: 260px; flex-shrink: 0;">
        </el-date-picker>

        <!-- 用户选择下拉框 -->
        <el-select
          v-if="hasUserSelectPermission"
          v-model="params.query.userId"
          clearable
          placeholder="请选择用户"
          @clear="userClear"
          @change="userSearch"
          style="width: 160px; flex-shrink: 0;">
          <el-option
            v-for="user in userList"
            :key="user.userId"
            :label="user.userAccount"
            :value="user.userId">
            <span>{{ user.userAccount }}</span>
          </el-option>
        </el-select>

        <!-- 关键字搜索 -->
        <el-input
          placeholder="请输入关键字搜索"
          v-model="params.query.selectdata"
          clearable
          @clear="contentClear"
          style="width: 260px; flex-shrink: 0;">
          <el-button slot="append" icon="el-icon-search" @click="contentSearch"></el-button>
        </el-input>

        <!-- 重置按钮 -->
        <el-button type="primary" @click="clear" style="flex-shrink: 0; margin: 0;">重置</el-button>

        <!-- 占位空间：自动占满剩余空间 -->
        <div style="flex: 1;"></div>

        <!-- 刷新按钮：永远在最右边 -->
        <div style="align-self: flex-start; flex-shrink: 0;">
          <el-button type="text" icon="el-icon-refresh" @click="getLogPage" style="color: gray; padding: 0;">刷新</el-button>
        </div>
      </div>

      <!-- 日志列表区域 -->
      <div class="table-container">
        <el-table :data="logList"
                  border
                  stripe
                  height="100%"
                  :row-style="{height: '6vh'}"
                  :cell-style="{padding: '0'}">
          <el-table-column label="日期"
                           prop="createdTime" :min-width="152" align="center" show-overflow-tooltip>
            <template #default="{ row }">
              {{ formatDateTime(row.createdTime) }}
            </template>
          </el-table-column>
          <el-table-column label="账号"
                           prop="userAccount" :min-width="180" align="center" show-overflow-tooltip></el-table-column>
          <el-table-column label="操作类型"
                           prop="type" :min-width="180" align="center" show-overflow-tooltip></el-table-column>
          <el-table-column label="日志"
                           prop="content" :min-width="600" header-align="center" show-overflow-tooltip></el-table-column>
        </el-table>
      </div>

      <!-- 分页区域 -->
      <div>
        <el-pagination
          ref="pagination"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="params.current"
          :page-sizes="[50, 100, 1000]"
          :page-size="params.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total">
        </el-pagination>
      </div>
    </el-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import paginationMixin from '@/components/mixins/pagination'

export default {
  mixins: [paginationMixin],
  data () {
    return {
      // 获取测试项目列表的参数对象
      params: {
        query: {
          dateRange: [], // 初始设置为空数组
          selectdata: '',
          userId: ''
        },
        // 当前的页数
        current: 1,
        // 当前每页显示多少条数据
        size: 50
      },
      userList: [],
      dateRange: [], // 用于日期条件查询
      logList: [],
      total: 0
    }
  },
  computed: {
    ...mapGetters('globalUserInfo', [
      'userAccount',
      'userId',
      'isSuperAdmin',
      'isNormalAdmin'
    ]),
    // 是否有用户选择权限（超级管理员或管理员）
    hasUserSelectPermission() {
      return this.isSuperAdmin || this.isNormalAdmin
    }
  },
  async created() {
    const { params } = await this.$store.dispatch('tabs/restorePageCache', {
      key: 'operationLogCache',
      defaultParams: this.params
    })

    // 缓存 userId 并清空
    const cachedUserId = params.query.userId
    params.query.userId = ''

    this.params = params
    this.dateRange = params.query.dateRange || []

    // 先加载用户列表
    await this.getUserList()

    // 恢复 userId 后再查询日志
    if (cachedUserId) {
      this.params.query.userId = cachedUserId
    }
    await this.getLogPage()
  },

  methods: {
    ...mapActions('tabs', ['savePageCache']),

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      if (typeof dateTime === 'string' && dateTime.includes('T')) {
        return dateTime.replace('T', ' ').substring(0, 19)
      }
      return dateTime
    },

    async getLogPage() {
      // 权限控制逻辑保持不变
      // 超级管理员：可以查看所有用户日志（不设置userId，让后端查全部）
      // 管理员：不设置userId，后端根据当前用户ID自动查询自己及子账号的日志
      // 普通用户：只能查看自己的日志，设置userId为自己

      if (!this.isSuperAdmin && !this.isNormalAdmin) {
        // 普通用户：只能看自己的日志
        // 但如果用户主动筛选了其他用户（不应该发生，这里是兜底）
        if (!this.params.query.userId) {
          this.params.query.userId = this.userId
        }
      }
      // 管理员和超级管理员：保留用户主动筛选的条件（如果有）
      // 不要覆盖 params.query.userId，因为可能是用户主动选择的

      const { data: result } = await this.$http.post('/log/page', this.params)
      this.logList = result.data.logPage.records
      this.total = result.data.logPage.total

      // 缓存分页 + 日期 + 用户 + 内容查询
      this.savePageCache({
        key: 'operationLogCache',
        params: this.params
      })

      // 超级管理员或管理员才获取用户列表
      if (this.hasUserSelectPermission) {
        this.getUserList()
      }
    },

    async getUserList() {
      // 调用后端接口获取用户列表（超级管理员获取所有用户，管理员获取其子账号）
      const { data: result } = await this.$http.get('/user/childAccounts')
      if (result.status !== 200) return this.$message.error(result.message)
      this.userList = (result.data.records || []).map(user => ({
        userId: user.userId,
        userAccount: user.userAccount
      }))
    },

    // 日期模糊查询
    handleDateChange(value) {
      this.params.current = 1 // 重置页码
      this.params.query.dateRange = this.dateRange // 同步日期范围
      this.getLogPage() // 重新加载数据
    },

    // 用户查询（超级管理员或管理员使用）
    userSearch() {
      this.params.current = 1
      this.getLogPage()
    },

    // 模糊查询
    contentSearch() {
      this.params.current = 1
      this.getLogPage()
    },

    // 手动清除用户条件查询数据
    userClear() {
      this.userResetFlag()
      this.getLogPage() // 重新加载数据
    },

    // 手动清除内容条件查询数据
    contentClear() {
      this.contentResetFlag()
      this.getLogPage() // 重新加载数据
    },

    // 重置
    clear () {
      this.dateResetFlag()
      this.userResetFlag()
      this.contentResetFlag()
      this.getLogPage()
    },

    // 重置时间条件查询标志位
    dateResetFlag() {
      this.dateRange = null
      this.params.query.dateRange = null
      this.params.current = 1 // 重置页码
    },

    // 重置用户条件查询标志位
    userResetFlag() {
      this.params.query.userId = '' // 清空查询用户
      this.params.current = 1 // 重置页码
    },

    // 重置内容模糊查询标志位
    contentResetFlag() {
      this.params.query.selectdata = '' // 清空模糊查询内容
      this.params.current = 1 // 重置页码
    },

    // 监听 pagesize 改变的事件
    handleSizeChange (size) {
      this.params.size = size
      this.getLogPage()
    },

    // 监听 页码值 改变的事件
    handleCurrentChange (current) {
      this.params.current = current
      this.getLogPage()
    }
  }
}
</script>
